import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { Users, Plus, Edit, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = { email: '', full_name: '', phone: '', password: '', role: 'client' };

const UsersManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/users/`);
      setUsers(res.data);
    } catch {
      toast.error('Gagal memuat data user');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const openCreate = () => {
    setForm(emptyForm);
    setShowCreate(true);
  };

  const openEdit = (u) => {
    setEditUser(u);
    setForm({ email: u.email, full_name: u.full_name, phone: u.phone || '', password: '', role: u.role });
    setShowEdit(true);
  };

  const handleCreate = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/users/`, form);
      toast.success('User berhasil ditambahkan');
      setShowCreate(false);
      fetchUsers();
    } catch (err) {
      toast.error('Gagal tambah: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      await axios.put(`${API}/users/${editUser.id}`, payload);
      toast.success('User berhasil diupdate');
      setShowEdit(false);
      fetchUsers();
    } catch (err) {
      toast.error('Gagal update: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Hapus user ${u.full_name || u.email}?`)) return;
    try {
      await axios.delete(`${API}/users/${u.id}`);
      toast.success('User berhasil dihapus');
      fetchUsers();
    } catch (err) {
      toast.error('Gagal hapus: ' + (err.response?.data?.detail || err.message));
    }
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('id-ID', {
    day: 'numeric', month: 'short', year: 'numeric',
  });

  const roleLabel = (r) => r === 'super_admin' ? 'Super Admin' : r === 'admin' ? 'Admin' : r === 'client' ? 'Client' : r;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold font-['Outfit']">Manajemen User</h1>
        <Button className="rounded-full" onClick={openCreate}>
          <Plus className="h-4 w-4 mr-2" /> Tambah User
        </Button>
      </div>

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Users className="h-5 w-5" /> Daftar Pengguna</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Memuat data user...</div>
          ) : users.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">Belum ada pengguna</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3 font-semibold text-sm">Nama</th>
                    <th className="text-left p-3 font-semibold text-sm">Email</th>
                    <th className="text-left p-3 font-semibold text-sm">Role</th>
                    <th className="text-left p-3 font-semibold text-sm">Daftar</th>
                    <th className="text-right p-3 font-semibold text-sm">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b hover:bg-muted/50">
                      <td className="p-3 font-medium capitalize">{u.full_name || '-'}</td>
                      <td className="p-3 text-muted-foreground">{u.email}</td>
                      <td className="p-3">
                        <Badge variant={u.role === 'admin' || u.role === 'super_admin' ? 'default' : 'secondary'}>
                          {roleLabel(u.role)}
                        </Badge>
                      </td>
                      <td className="p-3 text-sm text-muted-foreground">{formatDate(u.created_at)}</td>
                      <td className="p-3 text-right">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(u)}><Edit className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(u)}><Trash2 className="h-4 w-4" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Modal */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Tambah User Baru</DialogTitle>
            <DialogDescription>Lengkapi data untuk membuat pengguna baru</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="user@email.com" required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="full_name">Nama Lengkap *</Label>
              <Input id="full_name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">No. HP *</Label>
              <Input id="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="08123456789" required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password *</Label>
              <Input id="password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Minimal 6 karakter" required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">Role *</Label>
              <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                <option value="client">Client</option>
                <option value="admin">Admin</option>
                <option value="super_admin">Super Admin</option>
                <option value="travel_partner">Travel Partner</option>
                <option value="hotel_partner">Hotel Partner</option>
                <option value="muthowif">Muthowif</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Batal</Button>
            <Button type="button" onClick={handleCreate} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Tambah
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>Ubah data pengguna (kosongkan password jika tidak diubah)</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="e_email">Email</Label>
              <Input id="e_email" value={form.email} disabled />
            </div>
            <div className="space-y-2">
              <Label htmlFor="e_full_name">Nama Lengkap</Label>
              <Input id="e_full_name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="e_phone">No. HP</Label>
              <Input id="e_phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="e_password">Password Baru (opsional)</Label>
              <Input id="e_password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Kosongkan jika tidak diubah" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="e_role">Role</Label>
              <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                <option value="client">Client</option>
                <option value="admin">Admin</option>
                <option value="super_admin">Super Admin</option>
                <option value="travel_partner">Travel Partner</option>
                <option value="hotel_partner">Hotel Partner</option>
                <option value="muthowif">Muthowif</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowEdit(false)}>Batal</Button>
            <Button type="button" onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Simpan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersManagement;
